// World games: a small kit for games played on foot through a whole world
// (a maze hunt, a scavenger run, a race), built from placeable objects.
//
// Every piece is a primitive with shape 'worldgame' and a `worldGame` config.
// Pieces that share the same `game` key make one game:
//
//   start        the console where a run begins (title, rules, limits, scoring)
//   collectible  an item to pick up (walk into it; touch or grab it in VR)
//   patrol       a hunter that walks a fixed path; touching it costs you
//   goal         the finish zone (the primitive's box)
//   scare        a jump-scare zone (the primitive's box) that pops an apparition
//   powerup      a pick-up with an effect: a crow feather freezes the hunters, a lantern lights the way
//   figure       a cutout figure standing in the world (watches you, or vanishes up close)
//
// This file is types + validation only (loaded by the world store), so it
// stays tiny and has no React / three.js imports.

export type WorldGameRole = 'start' | 'collectible' | 'patrol' | 'goal' | 'scare' | 'figure' | 'powerup';
export const WORLD_GAME_ROLES: WorldGameRole[] = ['start', 'collectible', 'patrol', 'goal', 'scare', 'figure', 'powerup'];

export type CollectibleLook = 'pumpkin' | 'golden' | 'candy' | 'gem' | 'star';
export type HunterLook = 'scarecrow' | 'ghost';
export type WorldGameTheme = 'harvest' | 'neon' | 'frost';

export interface WorldGameConfig {
  role: WorldGameRole;
  /** Which game this piece belongs to (pieces with the same key play together). */
  game: string;

  // ---- start ----
  title?: string;
  blurb?: string;
  /** How to play: one instruction per line. */
  howTo?: string;
  /** What the collectibles are called ("pumpkin" → "3 / 12 pumpkins"). */
  itemName?: string;
  /** Items needed to finish (0 = all of this game's collectibles). */
  required?: number;
  /** Seconds; 0 = no limit. */
  timeLimit?: number;
  /** Catches allowed; 0 = unlimited (each catch still costs the penalty). */
  lives?: number;
  /** What a catch costs: drop your latest item (it goes back to its spot) or add time. */
  penalty?: 'item' | 'time';
  /** Seconds added by a 'time' penalty (and always, on top, by an 'item' one with nothing to drop). */
  penaltySeconds?: number;
  /** Leaderboard id for the fastest times (an arcade_games row: 'harvest', 'worldgame'). */
  board?: string;
  theme?: WorldGameTheme;
  /** Allow the "Race" button (a shared countdown for everyone in the world). */
  race?: boolean;
  /** One-line goal, shown on the welcome card and launch card. */
  goal?: string;
  /** The rules (one per line, up to 3): welcome card, launch card, the sign by the start. */
  rules?: string;
  /** Show the welcome / how-to-play card on arrival, the rules sign and the way to the start. Default on. */
  showGuide?: boolean;

  // ---- collectible / patrol ----
  look?: CollectibleLook | HunterLook;

  // ---- patrol ----
  /** Waypoints, world [x, z]. The hunter walks them in order. */
  path?: [number, number][];
  /** true = back to the first point and round again; false = there and back. */
  loop?: boolean;
  /** Metres per second. */
  speed?: number;
  /** Seconds it stops to look around at each waypoint. */
  pause?: number;
  /** 0–1: where along its round it is at time 0 (spreads several hunters out). */
  phase?: number;
  /** Metres: how close counts as caught. */
  catchRadius?: number;

  // ---- scare ----
  /** Which apparition (0–2) in the sprite sheet. */
  frame?: number;
  /** Seconds before the same zone can scare the same player again. */
  cooldown?: number;
  /** Image URL: a sprite sheet of `cols` frames side by side (empty = the kit's ghosts, 3 frames). */
  imageUrl?: string;
  /** Frames across the image (1 = a single figure). */
  cols?: number;
  /** scare: how the apparition shows — 'ghost' rushes at you, 'appear' fades in and out ahead, 'dash' darts across your path. */
  kind?: 'ghost' | 'appear' | 'dash';

  // ---- collectible ----
  /** A bonus (e.g. the golden pumpkin): not needed to finish; takes this many seconds off your time. */
  bonusSeconds?: number;

  // ---- powerup ----
  power?: 'freeze' | 'lantern';
  /** How long the effect lasts (s). */
  seconds?: number;

  // ---- figure ----
  /** Height in metres. */
  height?: number;
  /** 'watch': always turns to face you; 'vanish': fades away when you come close. */
  behavior?: 'watch' | 'vanish';
}

export const WORLD_GAME_DEFAULTS = {
  title: 'World Hunt',
  blurb: 'Find every item and reach the finish before the hunters find you.',
  howTo: 'Walk into an item to collect it\nAvoid the hunters: a catch costs you an item\nReach the finish with everything',
  itemName: 'item',
  required: 0,
  timeLimit: 0,
  lives: 0,
  penalty: 'item' as const,
  penaltySeconds: 10,
  board: 'worldgame',
  theme: 'harvest' as WorldGameTheme,
  race: true,
  speed: 1.6,
  pause: 0.8,
  phase: 0,
  catchRadius: 1.5,
  cooldown: 25,
  loop: false,
};

const str = (v: unknown, max: number): string | undefined => (typeof v === 'string' ? v.slice(0, max) : undefined);
const num = (v: unknown, lo: number, hi: number): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : undefined;
const oneOf = <T extends string>(v: unknown, list: readonly T[]): T | undefined => (list.includes(v as T) ? (v as T) : undefined);

/** Validate a saved `worldGame` value. Anything malformed falls back to safe defaults; no role → null. */
export function parseWorldGame(v: unknown): WorldGameConfig | undefined {
  const o = v as Record<string, unknown> | null;
  if (!o || typeof o !== 'object') return undefined;
  const role = oneOf(o.role, WORLD_GAME_ROLES);
  if (!role) return undefined;
  const game = (str(o.game, 40) || 'game').trim() || 'game';
  const out: WorldGameConfig = { role, game };
  const set = <K extends keyof WorldGameConfig>(k: K, val: WorldGameConfig[K] | undefined) => { if (val !== undefined) out[k] = val; };
  set('title', str(o.title, 40));
  set('blurb', str(o.blurb, 140));
  set('howTo', str(o.howTo, 400));
  set('itemName', str(o.itemName, 24));
  set('required', num(o.required, 0, 200));
  set('timeLimit', num(o.timeLimit, 0, 3600));
  set('lives', num(o.lives, 0, 20));
  set('penalty', oneOf(o.penalty, ['item', 'time'] as const));
  set('penaltySeconds', num(o.penaltySeconds, 0, 300));
  set('board', str(o.board, 40));
  set('theme', oneOf(o.theme, ['harvest', 'neon', 'frost'] as const));
  if (typeof o.race === 'boolean') out.race = o.race;
  set('look', oneOf(o.look, ['pumpkin', 'golden', 'candy', 'gem', 'star', 'scarecrow', 'ghost'] as const));
  if (Array.isArray(o.path)) {
    const pts = o.path
      .filter((p): p is [number, number] => Array.isArray(p) && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]))
      .slice(0, 64)
      .map((p) => [+(+p[0]).toFixed(3), +(+p[1]).toFixed(3)] as [number, number]);
    out.path = pts;
  }
  if (typeof o.loop === 'boolean') out.loop = o.loop;
  set('speed', num(o.speed, 0.1, 12));
  set('pause', num(o.pause, 0, 30));
  set('phase', num(o.phase, 0, 1));
  set('catchRadius', num(o.catchRadius, 0.3, 6));
  set('frame', num(o.frame, 0, 2));
  set('cooldown', num(o.cooldown, 1, 600));
  set('imageUrl', str(o.imageUrl, 500));
  set('cols', num(o.cols, 1, 8));
  set('kind', oneOf(o.kind, ['ghost', 'appear', 'dash'] as const));
  set('height', num(o.height, 0.3, 12));
  set('bonusSeconds', num(o.bonusSeconds, 0, 120));
  set('power', oneOf(o.power, ['freeze', 'lantern'] as const));
  set('seconds', num(o.seconds, 1, 60));
  set('behavior', oneOf(o.behavior, ['watch', 'vanish'] as const));
  set('goal', str(o.goal, 160));
  set('rules', str(o.rules, 400));
  if (typeof o.showGuide === 'boolean') out.showGuide = o.showGuide;
  return out;
}
